<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { ChevronDown, ChevronUp, Lock } from 'lucide-vue-next';
import { ref, watch } from 'vue';

interface Status {
  text: string;
  type: string;
}

interface Props {
  defaultExpanded?: boolean;
  isLocked?: boolean;
  status?: Status;
  title: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultExpanded: true,
  isLocked: false,
  status: undefined,
});

const emit = defineEmits<{
  'update:expanded': [value: boolean];
}>();

const isExpanded = ref(props.isLocked ? false : props.defaultExpanded);

watch(() => props.defaultExpanded, (newValue) => {
  if (!props.isLocked && typeof newValue === 'boolean') {
    isExpanded.value = newValue;
  }
}, { immediate: false });

function handleToggle() {
  if (!props.isLocked) {
    isExpanded.value = !isExpanded.value;
    emit('update:expanded', isExpanded.value);
  }
}

function getStatusColorClass(type: string): string {
  const colorMap: Record<string, string> = {
    informative: 'blue',
    negative: 'red',
    neutral: 'gray',
    positive: 'green',
    warning: 'yellow',
  };

  return colorMap[type] || 'gray';
}
</script>

<template>
  <div class="card">
    <div
      class="card-header d-flex justify-content-between align-items-center"
      :class="{ 'cursor-pointer': !isLocked }"
      @click="handleToggle"
      @keydown.enter="handleToggle"
      @keydown.space.prevent="handleToggle"
      :tabindex="!isLocked ? 0 : -1"
      :role="!isLocked ? 'button' : undefined"
      :aria-expanded="!isLocked ? isExpanded : undefined"
    >
      <div class="d-flex align-items-center gap-2">
        <h3 class="card-title mb-0">{{ title }}</h3>
        <span
          v-if="status"
          class="badge"
          :class="`bg-${getStatusColorClass(status.type)}-lt`"
        >
          {{ status.text }}
        </span>
      </div>

      <div class="d-flex align-items-center gap-2">
        <Lock v-if="isLocked" class="icon icon-tabler text-muted" />

        <template v-else>
          <ChevronDown v-if="!isExpanded" class="icon icon-tabler text-muted" />
          <ChevronUp v-if="isExpanded" class="icon icon-tabler text-muted" />
        </template>
      </div>
    </div>

    <div v-if="isExpanded || isLocked" class="card-body">
      <div v-if="isLocked" class="text-center text-muted py-4">
        <Lock class="icon icon-lg mb-2 text-muted" />
        <p class="mb-0">Este conteúdo estará disponível em etapas posteriores do processo.</p>
      </div>
      <slot v-else />
    </div>
  </div>
</template>

<style scoped lang="scss">
.cursor-pointer {
  cursor: pointer;
}

.icon-tabler {
  width: 1.25rem;
  height: 1.25rem;
}

.icon-lg {
  width: 2rem;
  height: 2rem;
}

.card-header:hover.cursor-pointer {
  background-color: var(--tblr-bg-surface-secondary);
}

</style>
